import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import DropDownMenu from 'Components/DropDownMenu';
import BlackFridayBanner from 'Components/Header/BlackFridayBanner';
import UIButton from 'Components/UIComponents/UIButton';
import { Location } from 'history';
import { useLogout } from 'Hooks/Auth';
import useIsMobile from 'Hooks/Common/useIsMobile';
import { PlanIds, PlanTypes, signatureLimitedPlans } from 'Interfaces/Billing';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { User, UserStatuses } from 'Interfaces/User';
import Toast from 'Services/Toast';
import { IS_BLACK_FRIDAY, IS_END_OF_YEAR } from 'Utils/constants';
import { selectUser, selectUserSignatureUsage } from 'Utils/selectors';

import DropDownUser from '../DropDownUser';

import AddTeamMemberButton from './AddTeamMemberButton';
import EndOfYearBanner from './EndOfYearBanner';
import TrialBanner from './TrialBanner';

import HelpIcon from 'Assets/images/icons/help-icon.svg';
import Logo from 'Assets/images/logo.svg';

const isSomeSale = IS_BLACK_FRIDAY || IS_END_OF_YEAR;

export interface HeaderProps {
  location: Location;
  isActionHidden?: boolean;
  headerClassName?: string;
}

function Header({ location, headerClassName, isActionHidden = false }: HeaderProps) {
  const [logout] = useLogout();
  const { isTrialUsed, teamId, plan, status: userStatus }: User = useSelector(selectUser);
  const userSignatureUsage = useSelector(selectUserSignatureUsage);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (userStatus === UserStatuses.FREEZE) {
      Toast.warn(
        'Your current subscription has not been paid; the account has been disabled. Please check your payment method.',
        { toastId: 'unpaid_toast' },
      );
    }
  }, [userStatus]);

  const isSignaturesLimited = useMemo(
    () => signatureLimitedPlans.includes(plan?.id as PlanIds) && !teamId,
    [plan, teamId],
  );

  const isTrialAvailable = useMemo(
    () => plan?.type === PlanTypes.FREE && !isTrialUsed && !teamId,
    [plan, isTrialUsed, teamId],
  );

  return (
    <header className="header__wrapper">
      {!isActionHidden && (
        <>
          <BlackFridayBanner />
          <EndOfYearBanner />
        </>
      )}
      {!isActionHidden && isTrialAvailable && !isSomeSale && <TrialBanner />}
      <div className={classNames('header', headerClassName)}>
        <Link to={!isActionHidden ? AuthorizedRoutePaths.BASE_PATH : location}>
          <img src={Logo} alt="Signaturely" />
        </Link>
        {!isMobile && isSignaturesLimited && !isActionHidden && (
          <div className="header__month-wrapper">
            <div className="header__counter-wrapper">
              <p className="header__month-counter">
                {userSignatureUsage}
                <span className="header__left-month-text">this month</span>
              </p>
            </div>

            <Link to={AuthorizedRoutePaths.SETTINGS_BILLING_PLAN}>
              <UIButton priority="primary" title="Upgrade" />
            </Link>
          </div>
        )}
        {!isMobile &&
          !isActionHidden &&
          (isSignaturesLimited || (isTrialAvailable && isSomeSale)) && (
            <div className="header__divider" />
          )}
        <div className="header__action-wrapper">
          {isMobile ? (
            <DropDownMenu
              location={location}
              handleLogout={logout}
              isSignaturesLimited={isSignaturesLimited}
              isActionHidden={isActionHidden}
            />
          ) : (
            <>
              <AddTeamMemberButton />
              <DropDownUser
                location={location}
                handleLogout={logout}
                isActionHidden={isActionHidden}
              />
              <a
                href="https://help.signaturely.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="header__help header__help--stroke"
              >
                <ReactSVG src={HelpIcon} className="header__help-icon" />
              </a>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
